// Minimal, dependency-free, XSS-safe Markdown -> HTML renderer used for CMS
// learning content (PRD Module 3.9 rich text). Raw HTML in the source is ESCAPED
// first, so author input can never inject markup; only the whitelisted Markdown
// constructs below produce tags. No external libraries (CSP-safe).

import { mediaUrl } from './mediaUrl';
import { renderChartFigure } from './chart';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Inverse of escapeHtml, for content that must round-trip back to its raw form
// before being handed to KaTeX (needs real `<`/`>`) or JSON.parse (chart data).
// &amp; last so an already-escaped "&lt;" isn't double-unescaped.
function unescapeHtml(s: string): string {
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&amp;/g, '&');
}

// KaTeX needs the raw TeX source, not the escaped fragment inline() works on.
// We stash it in a data-attribute (HTML-attribute-escaped) and render visible
// TeX as a text fallback; RichText lazily typesets these client-side so KaTeX
// is never loaded unless a question actually uses math.
function mathSpan(rawTexEscapedFragment: string, display: boolean): string {
  const raw = unescapeHtml(rawTexEscapedFragment);
  const safe = escapeHtml(raw);
  const cls = display ? 'katex-target katex-block' : 'katex-target';
  const tag = display ? 'div' : 'span';
  return `<${tag} class="${cls}" data-tex="${safe}">${safe}</${tag}>`;
}

// Image sources we will emit. Anything else renders as inert text rather than a
// tag: `javascript:` and `data:text/html` are the obvious attacks, but a bare
// remote URL is also refused so content can't silently beacon to a third party.
// Uploads are same-origin ('/uploads/...'); data:image is allowed for previews
// that haven't been uploaded yet.
function safeImageSrc(src: string): string | null {
  const s = src.trim();
  if (s.startsWith('/uploads/') && !s.includes('..')) return s;
  if (/^data:image\/(png|jpeg|webp|gif);base64,[a-z0-9+/=]+$/i.test(s)) return s;
  return null;
}

// Inline: images, bold, italic, inline code, links (href sanitised).
function inline(text: string): string {
  let t = text;
  // Images before links — `![alt](src)` would otherwise match the link rule.
  t = t.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (whole, alt, src) => {
    const safe = safeImageSrc(src);
    if (!safe) return whole; // already escaped upstream, so this is inert text
    return `<img src="${mediaUrl(safe)}" alt="${alt}" loading="lazy" class="content-image" />`;
  });
  // Math before code/bold/italic — TeX commonly contains backticks and asterisks
  // that would otherwise be misread as those constructs. $$..$$ (display, inline
  // position) before $..$ so a display pair isn't split into two empty matches.
  t = t.replace(/\$\$([^$\n]+)\$\$/g, (_, tex) => mathSpan(tex, true));
  t = t.replace(/\$([^$\n]+)\$/g, (_, tex) => mathSpan(tex, false));
  // Additional LaTeX delimiters \( ... \) and \[ ... \] inline
  t = t.replace(/\\\[([^\]]+)\\\]/g, (_, tex) => mathSpan(tex, true));
  t = t.replace(/\\\(([^)]+)\\\)/g, (_, tex) => mathSpan(tex, false));
  t = t.replace(/`([^`]+)`/g, (_, c) => `<code>${c}</code>`);
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  t = t.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label, href) => {
    const safe = /^(https?:|mailto:)/i.test(href) ? href : '#';
    return `<a href="${safe}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  return t;
}

/**
 * Inline-only render for short content that must not gain block wrappers —
 * answer options and other content that lives inside buttons.
 * Same escaping and same image allowlist as renderMarkdown.
 * Question stems must use renderMarkdown so tables/charts render.
 */
export function renderInlineMarkdown(src: string): string {
  if (!src) return '';
  // Normalize double-escaped math delimiters (e.g. \\( -> \()
  const normalized = src.replace(/\\\\([()\[\]])/g, '\\$1');
  return inline(escapeHtml(normalized).replace(/\r\n/g, '\n').replace(/\n/g, '<br />'));
}

/**
 * Plain-text form for compact lists (search results, revision queues, analytics)
 * where a full image would be noise but raw `![alt](src)` syntax would leak.
 * Keeps the alt text so the row still describes itself.
 */
export function stripImages(src: string): string {
  if (!src) return '';
  return src
    .replace(/!\[([^\]]*)\]\([^)\s]+\)/g, (_, alt) => (alt ? `[${alt}]` : '[image]'))
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** True when the source embeds at least one image we would actually render. */
export function hasImage(src: string): boolean {
  if (!src) return false;
  const m = src.match(/!\[[^\]]*\]\(([^)\s]+)\)/g);
  return Boolean(m && m.some((tag) => {
    const src2 = /\(([^)\s]+)\)/.exec(tag)?.[1] ?? '';
    return safeImageSrc(src2) !== null;
  }));
}

/** True when the source has an image, formula, table, or chart worth previewing. */
export function hasRichPreview(src: string): boolean {
  if (!src) return false;
  return hasImage(src)
    || /\$\$[^$]+\$\$|\$[^$\n]+\$/.test(src)
    || /\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/.test(src)
    || /```chart\s*\n/.test(src)
    || /^\s*\|.+\|\s*$/m.test(src);
}

// --- Tables ---------------------------------------------------------------
// GFM-style pipe tables, plus a small merge extension: a body cell containing
// exactly `^^` merges into the cell above (extends its rowspan); a cell
// containing exactly `<<` merges into the cell to its left (extends its
// colspan). TableInsertButton emits this same syntax.

function isTableSeparatorLine(line: string): boolean {
  const t = line.trim();
  return /^[|:\-\s]+$/.test(t) && t.includes('-') && t.includes('|');
}

function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function parseAlign(cell: string): 'left' | 'center' | 'right' | null {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

function renderTable(rawRows: string[][], aligns: (string | null)[]): string {
  const cols = aligns.length;
  const rows = rawRows.map((r) => {
    const padded = r.slice(0, cols);
    while (padded.length < cols) padded.push('');
    return padded;
  });
  const rowCount = rows.length;
  const rowspan: number[][] = rows.map((r) => r.map(() => 1));
  const colspan: number[][] = rows.map((r) => r.map(() => 1));
  const skip: boolean[][] = rows.map((r) => r.map(() => false));

  const lastOriginRow: number[] = new Array(cols).fill(-1);
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = rows[r][c].trim();
      if (cell === '^^' && lastOriginRow[c] !== -1) {
        rowspan[lastOriginRow[c]][c] += 1;
        skip[r][c] = true;
      } else if (cell !== '^^') {
        lastOriginRow[c] = r;
      }
    }
  }
  for (let r = 0; r < rowCount; r++) {
    let lastOriginCol = -1;
    for (let c = 0; c < cols; c++) {
      if (skip[r][c]) { lastOriginCol = -1; continue; }
      const cell = rows[r][c].trim();
      if (cell === '<<' && lastOriginCol !== -1) {
        colspan[r][lastOriginCol] += 1;
        skip[r][c] = true;
      } else {
        lastOriginCol = c;
      }
    }
  }

  const renderRow = (r: number, tag: 'th' | 'td') => {
    let rowHtml = '<tr>';
    for (let c = 0; c < cols; c++) {
      if (skip[r][c]) continue;
      const cell = rows[r][c].trim();
      const rs = rowspan[r][c];
      const cs = colspan[r][c];
      const align = aligns[c];
      const attrs = `${rs > 1 ? ` rowspan="${rs}"` : ''}${cs > 1 ? ` colspan="${cs}"` : ''}${align ? ` style="text-align:${align}"` : ''}`;
      rowHtml += `<${tag}${attrs}>${inline(cell)}</${tag}>`;
    }
    return `${rowHtml}</tr>`;
  };

  const head = renderRow(0, 'th');
  const body = rows.slice(1).map((_, idx) => renderRow(idx + 1, 'td')).join('');
  return `<div class="content-table-wrap"><table class="content-table"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
}

export function renderMarkdown(src: string): string {
  if (!src) return '';
  // Normalize double-escaped math delimiters (e.g. \\( -> \()
  // and insert double newlines before inline headers/bullets if squeezed onto single lines
  const normalized = src
    .replace(/\\\\([()\[\]])/g, '\\$1')
    .replace(/([^\n])\s*(#{1,6}\s+)/g, '$1\n\n$2')
    .replace(/([^\n])\s*(\d+\.\s+\*\*)/g, '$1\n\n$2');

  const lines = escapeHtml(normalized).replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let inUl = false;
  let inOl = false;
  let inCode = false;
  const closeLists = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Fenced blocks: ```chart (structured chart data), plain ``` code, or a
    // $$ math fence — all consume lines until their matching close.
    if (/^```chart/i.test(trimmed)) {
      closeLists();
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { body.push(lines[i]); i++; }
      out.push(renderChartFigure(unescapeHtml(body.join('\n'))));
      continue; // outer i++ steps past the closing ```
    }
    if (/^```/.test(trimmed)) {
      if (inCode) { out.push('</code></pre>'); inCode = false; }
      else { closeLists(); out.push('<pre><code>'); inCode = true; }
      continue;
    }
    if (inCode) { out.push(line); continue; }

    if (/^\$\$\s*$/.test(trimmed)) {
      closeLists();
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== '$$') { body.push(lines[i]); i++; }
      out.push(mathSpan(body.join('\n'), true));
      continue;
    }

    // Support \[ and \] as multiline display math blocks
    if (/^\\\[\s*$/.test(trimmed)) {
      closeLists();
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\\\]\s*$/.test(lines[i].trim())) {
        body.push(lines[i]);
        i++;
      }
      out.push(mathSpan(body.join('\n'), true));
      continue;
    }

    if (/^\s*$/.test(line)) { closeLists(); continue; }

    // Table: a `|` row followed by a `-`/`:`/`|` separator row.
    if (/^\s*\|/.test(line) && i + 1 < lines.length && isTableSeparatorLine(lines[i + 1])) {
      closeLists();
      const alignCells = splitTableRow(lines[i + 1]);
      const aligns = alignCells.map(parseAlign);
      const rows: string[][] = [splitTableRow(line)];
      i += 2;
      while (i < lines.length && /^\s*\|/.test(lines[i])) { rows.push(splitTableRow(lines[i])); i++; }
      i--; // outer i++ re-steps onto the line after the table
      out.push(renderTable(rows, aligns));
      continue;
    }

    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) { closeLists(); const lvl = h[1].length; out.push(`<h${lvl}>${inline(h[2])}</h${lvl}>`); continue; }

    const ul = /^\s*[-*]\s+(.*)$/.exec(line);
    if (ul) { if (!inUl) { closeLists(); out.push('<ul>'); inUl = true; } out.push(`<li>${inline(ul[1])}</li>`); continue; }

    const ol = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (ol) { if (!inOl) { closeLists(); out.push('<ol>'); inOl = true; } out.push(`<li>${inline(ol[1])}</li>`); continue; }

    closeLists();
    out.push(`<p>${inline(line)}</p>`);
  }
  if (inCode) out.push('</code></pre>');
  closeLists();
  return out.join('\n');
}
